'use client';

import { c as _c } from "react/compiler-runtime";
import { useMemo, useState } from 'react';
import { QUADRANTS, QUADRANT_LABELS, Quadrant, RINGS, RING_LABELS, Technology } from './types';
export type NumberedTechnology = Technology & {
  number: number;
};
interface RadarVisualizationProps {
  /** Every technology, numbered. Positions are derived from the full list so
   *  blips stay put when a filter is applied. */
  technologies: NumberedTechnology[];
  /** Ids filtered out; their blips are drawn faint and are not interactive. */
  hiddenIds?: ReadonlySet<string>;
}
const SIZE = 840;
const CENTER = SIZE / 2;
// Ring edges from the centre out. Adopt is widest because it holds the most
// entries and a quarter of a small circle has little room.
const RING_EDGES = [0, 150, 235, 305, 365];
const BLIP_RADIUS = 10;
const ROW_GAP = 25;
// Degrees kept clear on each side of a quadrant line
const EDGE_MARGIN = 7;

// Angles in SVG space: 0° points right and grows clockwise
const QUADRANT_ANGLES: Record<Quadrant, [number, number]> = {
  platforms: [0, 90],
  // bottom-right
  tools: [90, 180],
  // bottom-left
  techniques: [180, 270],
  // top-left
  languages: [270, 360] // top-right
};
const QUADRANT_LABEL_POSITIONS: Record<Quadrant, {
  x: number;
  y: number;
  anchor: 'start' | 'end';
}> = {
  techniques: {
    x: 54,
    y: 40,
    anchor: 'start'
  },
  languages: {
    x: SIZE - 54,
    y: 40,
    anchor: 'end'
  },
  tools: {
    x: 54,
    y: SIZE - 28,
    anchor: 'start'
  },
  platforms: {
    x: SIZE - 54,
    y: SIZE - 28,
    anchor: 'end'
  }
};
interface Blip {
  tech: NumberedTechnology;
  x: number;
  y: number;
}
const toRadians = (degrees: number) => degrees * Math.PI / 180;

/**
 * Lay one quadrant/ring group out on concentric rows, outer row first, so
 * blips never overlap and the result is the same on every render.
 */
function placeGroup(group: NumberedTechnology[], inner: number, outer: number, [start, end]: [number, number]): Blip[] {
  const sweep = end - start - EDGE_MARGIN * 2;
  const rows: {
    radius: number;
    capacity: number;
  }[] = [];
  for (let radius = outer - 17; radius > inner + 14; radius -= ROW_GAP) {
    const arcLength = toRadians(sweep) * radius;
    rows.push({
      radius,
      capacity: Math.max(1, Math.floor(arcLength / ROW_GAP))
    });
  }
  const total = rows.reduce((sum, row) => sum + row.capacity, 0);
  const counts = rows.map(row => Math.min(row.capacity, Math.ceil(group.length * row.capacity / total)));
  // Rounding up can over-allocate; take the surplus from the fullest rows
  let surplus = counts.reduce((sum, count) => sum + count, 0) - group.length;
  while (surplus > 0) {
    let fullest = 0;
    counts.forEach((count, i) => {
      if (count / rows[i].capacity > counts[fullest] / rows[fullest].capacity) fullest = i;
    });
    counts[fullest] -= 1;
    surplus -= 1;
  }
  const blips: Blip[] = [];
  let next = 0;
  rows.forEach((row, i) => {
    for (let j = 0; j < counts[i] && next < group.length; j++) {
      const angle = toRadians(start + EDGE_MARGIN + sweep * (j + 0.5) / counts[i]);
      blips.push({
        tech: group[next++],
        x: CENTER + row.radius * Math.cos(angle),
        y: CENTER + row.radius * Math.sin(angle)
      });
    }
  });
  return blips;
}
export function RadarVisualization(t0) {
  const $ = _c(25);
  const {
    technologies,
    hiddenIds
  } = t0;
  const [activeId, setActiveId] = useState(null);
  let t1;
  if ($[0] !== technologies) {
    t1 = QUADRANTS.flatMap(quadrant => RINGS.flatMap((ring, ringIndex) => placeGroup(technologies.filter(tech => tech.quadrant === quadrant && tech.ring === ring), RING_EDGES[ringIndex], RING_EDGES[ringIndex + 1], QUADRANT_ANGLES[quadrant])));
    $[0] = technologies;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const blips = t1;
  let t2;
  if ($[2] !== activeId || $[3] !== blips) {
    let t3;
    if ($[5] !== activeId) {
      t3 = blip => blip.tech.id === activeId;
      $[5] = activeId;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    t2 = blips.find(t3);
    $[2] = activeId;
    $[3] = blips;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const active = t2;
  const outerEdge = RING_EDGES[RING_EDGES.length - 1];
  const t3 = `Radar of ${technologies.length} technologies across four quadrants and four rings`;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [...RINGS].reverse().map(_temp);
    t5 = <line className="axis" x1={CENTER - outerEdge} y1={CENTER} x2={CENTER + outerEdge} y2={CENTER} />;
    t6 = <line className="axis" x1={CENTER} y1={CENTER - outerEdge} x2={CENTER} y2={CENTER + outerEdge} />;
    t7 = RINGS.map(_temp2);
    t8 = QUADRANTS.map(_temp3);
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
    $[11] = t8;
  } else {
    t4 = $[7];
    t5 = $[8];
    t6 = $[9];
    t7 = $[10];
    t8 = $[11];
  }
  let t9;
  if ($[12] !== blips || $[13] !== hiddenIds) {
    let t10;
    if ($[15] !== hiddenIds) {
      t10 = t11 => {
        const {
          tech: tech_0,
          x: x_0,
          y: y_0
        } = t11;
        const className = tech_0.isNew ? "blip new" : "blip";
        const mark = <><circle cx={x_0} cy={y_0} r={BLIP_RADIUS} /><text x={x_0} y={y_0 + 3.2} textAnchor="middle">{tech_0.number}</text></>;
        if (hiddenIds?.has(tech_0.id)) {
          return <g key={tech_0.id} className={`${className} off`} aria-hidden="true">{mark}</g>;
        }
        return <a key={tech_0.id} href={`#tech-${tech_0.id}`} className={className} aria-label={`${tech_0.number}. ${tech_0.name}, ${RING_LABELS[tech_0.ring]}${tech_0.isNew ? ", new" : ""}`} onMouseEnter={() => setActiveId(tech_0.id)} onMouseLeave={() => setActiveId(null)} onFocus={() => setActiveId(tech_0.id)} onBlur={() => setActiveId(null)}>{mark}</a>;
      };
      $[15] = hiddenIds;
      $[16] = t10;
    } else {
      t10 = $[16];
    }
    t9 = blips.map(t10);
    $[12] = blips;
    $[13] = hiddenIds;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[17] !== t3 || $[18] !== t9) {
    t10 = <svg viewBox={`0 0 ${SIZE} ${SIZE}`} role="group" aria-label={t3}>{t4}{t5}{t6}{t7}{t8}{t9}</svg>;
    $[17] = t3;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== active) {
    t11 = active && <div className={active.x > CENTER ? "tip left" : "tip"} style={{
      left: `${active.x / SIZE * 100}%`,
      top: `${active.y / SIZE * 100}%`
    }} role="status"><strong>{active.tech.name}</strong><span>{active.tech.description}</span><span className="k">{RING_LABELS[active.tech.ring]}{active.tech.isNew && " \xB7 New"}</span></div>;
    $[20] = active;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t10 || $[23] !== t11) {
    t12 = <div className="radar"><div className="radar-inner">{t10}{t11}</div></div>;
    $[22] = t10;
    $[23] = t11;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  return t12;
}
function _temp3(quadrant_0) {
  const {
    x,
    y,
    anchor
  } = QUADRANT_LABEL_POSITIONS[quadrant_0];
  return <a key={quadrant_0} href={`#quadrant-${quadrant_0}`}><text className="quadrant-label" x={x} y={y} textAnchor={anchor}>{QUADRANT_LABELS[quadrant_0]}</text></a>;
}
function _temp2(ring_1, i_0) {
  return <text key={ring_1} className="ring-label" x={CENTER + (RING_EDGES[i_0] + RING_EDGES[i_0 + 1]) / 2} y={CENTER - 6} textAnchor="middle" aria-hidden="true">{RING_LABELS[ring_1]}</text>;
}
function _temp(ring_0, i) {
  const ringIndex_0 = RINGS.length - 1 - i;
  return <circle key={ring_0} className={ringIndex_0 % 2 === 1 ? "band alt" : "band"} cx={CENTER} cy={CENTER} r={RING_EDGES[ringIndex_0 + 1]} />;
}